/**
 * @fileoverview Sales CRM organisation details API service.
 * @description Calls the backend's organisation details endpoints (Confluence 3.5.11.25-.26): read
 * the single record of legal, billing and bank details that Send Payment Details emails to a bank
 * (GET /sales/organisation-details; 409/4120 until it is first saved) and create or replace it (PUT
 * /sales/organisation-details; Super Admin only, needs a recent MFA sign-in). A save sends the
 * `od1.` version the user read as a strong `If-Match` entity tag, except the very first save, which
 * sends none.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { httpClient } from '@/shared/api/http-client'

import type {
  OrganisationDetails,
  OrganisationDetailsInput,
} from '@/features/sales/types/my-page.types'

/** @description The organisation details' URL. */
const ORGANISATION_DETAILS_URL = '/sales/organisation-details'

/**
 * @description Fetches the organisation details, bank account unmasked.
 * @returns {Promise<OrganisationDetails>} The details and their `od1.` version.
 */
export async function getOrganisationDetails(): Promise<OrganisationDetails> {
  const { data } = await httpClient.get<OrganisationDetails>(ORGANISATION_DETAILS_URL)
  return data
}

/**
 * @description Saves the organisation details: the first save creates them, every later one
 * replaces them. A field left null or absent is cleared.
 * @param {OrganisationDetailsInput} input - The full details.
 * @param {string | null} version - The `od1.` version the user read, or null for the first save.
 * @returns {Promise<OrganisationDetails>} The saved details and their new version.
 */
export async function saveOrganisationDetails(
  input: OrganisationDetailsInput,
  version: string | null,
): Promise<OrganisationDetails> {
  const { data } = await httpClient.put<OrganisationDetails>(
    ORGANISATION_DETAILS_URL,
    input,
    version === null ? {} : { headers: { 'If-Match': `"${version}"` } },
  )
  return data
}
